import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import PropTypes from 'prop-types';
import { Spinner } from '@/ui';
import { homeForCapability } from './constants.js';
import { useRealLocation } from './realLocation.js';
import {
  consumeReturnTo,
  hasPendingReturnTo,
  saveReturnTo,
} from './returnTo.js';

export default function ProtectedRoute({
  component: Component,
  allowedCapabilities,
  loginTo = '/login',
}) {
  const {
    isAuthenticated,
    loading,
    activeCapability,
    sessionExpired,
    termsPending,
    membershipPending,
    memberships,
    defaultOrganizationId,
    selectOrganization,
  } = useAuth();
  const location = useLocation();
  const realLocation = useRealLocation() ?? location;
  const { pathname, search } = location;
  const currentPath = pathname + search;
  const savePath = realLocation.pathname + realLocation.search;
  const [returnTo, setReturnTo] = useState(null);
  const pendingReturn = returnTo && returnTo !== currentPath;
  const [checked, setChecked] = useState(() => !hasPendingReturnTo());
  const wasAuthenticated = useRef(false);
  const candidates =
    isAuthenticated &&
    !loading &&
    allowedCapabilities &&
    !allowedCapabilities.includes(activeCapability)
      ? (memberships ?? []).filter((m) =>
          allowedCapabilities.includes(m.capabilities?.[0]),
        )
      : [];
  const targetOrgId = (
    candidates.find((m) => m.organizationId === defaultOrganizationId) ??
    candidates[0]
  )?.organizationId;

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      setReturnTo(null);
      setChecked(false);
      if (
        !wasAuthenticated.current ||
        sessionExpired ||
        termsPending ||
        membershipPending
      )
        saveReturnTo(savePath);
      return;
    }
    wasAuthenticated.current = true;
    if (checked) return;
    const saved = consumeReturnTo();
    setChecked(true);
    setReturnTo((previous) =>
      saved && saved !== currentPath
        ? saved
        : previous === currentPath
          ? null
          : previous,
    );
  }, [
    loading,
    isAuthenticated,
    currentPath,
    savePath,
    checked,
    sessionExpired,
    termsPending,
    membershipPending,
  ]);

  useEffect(() => {
    if (returnTo === currentPath) setReturnTo(null);
  }, [returnTo, currentPath]);

  useEffect(() => {
    if (targetOrgId && !pendingReturn && checked)
      selectOrganization(targetOrgId);
  }, [targetOrgId, pendingReturn, checked]);

  if (
    loading ||
    (isAuthenticated && !checked) ||
    (targetOrgId && !pendingReturn)
  ) {
    // parity TODO (LOW): full-page loading spinner — ideally replaced with a
    // skeleton-layout once a shared page skeleton exists.
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <Spinner size={64} />
      </div>
    );
  }

  if (isAuthenticated && pendingReturn)
    return <Navigate to={returnTo} replace />;

  if (!isAuthenticated) {
    // `sessionExpired` is set only after an in-session 401, so the notice shows
    // on a real expiry and not on a first visit or a logged-out refresh.
    // `termsPending` is set when the API refused the session for a missing
    // acceptance of the terms in force, and the login offers the way back.
    const loginState = {
      ...(sessionExpired && { sessionExpired: true }),
      ...(termsPending && { termsPending: true }),
      ...(membershipPending && { membershipPending: true }),
    };
    return <Navigate to={loginTo} state={loginState} />;
  }

  if (allowedCapabilities && !allowedCapabilities.includes(activeCapability)) {
    const home = homeForCapability(activeCapability);
    // Sin home propio (capability null / desconocida): NO redirigir — el destino
    // caería en un grupo que también la rechaza y entraría en loop. Mostramos un
    // aviso terminal.
    if (!home) {
      return (
        <div role="alert" className="pr-noaccess">
          <h2>Sin acceso</h2>
          <p>No tienes acceso para esta sección.</p>
        </div>
      );
    }
    return <Navigate to={home} replace />;
  }

  return <Component />;
}

ProtectedRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  allowedCapabilities: PropTypes.arrayOf(PropTypes.string),
  loginTo: PropTypes.string,
};
